import { forwardRef, useCallback, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import Carousel from 'react-slick';

const Carousels = forwardRef(
  (
    {
      children,
      dots = true,
      slideShow = 1,
      infinite = false,
      mobileSlideShow = 1,
      autoplay = true,
      pauseOnHover = true,
      autoplaySpeed = 3000,
      vertical = false,
      mobileSlideShowTwo = 1,
      center = false,
      arrows = false,
      sliderRef,
      afterChange
    }: any,
    ref: any
  ) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const syncSlideA11y = useCallback(() => {
      const root = wrapperRef.current;
      if (!root) return;

      const slides = root.querySelectorAll<HTMLElement>('.slick-slide');
      slides.forEach(slide => {
        const isHidden = slide.getAttribute('aria-hidden') === 'true';

        if (isHidden) {
          slide.setAttribute('inert', '');
        } else {
          slide.removeAttribute('inert');
        }

        const focusables = slide.querySelectorAll<HTMLElement>('a,button,input,select,textarea,[tabindex]');
        focusables.forEach(el => {
          if (isHidden) {
            if (!el.dataset.prevTabindex) {
              const prev = el.getAttribute('tabindex');
              el.dataset.prevTabindex = prev === null ? '__none__' : prev;
            }
            el.setAttribute('tabindex', '-1');
            return;
          }

          const prev = el.dataset.prevTabindex;
          if (!prev) return;

          if (prev === '__none__') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', prev);
          }
          delete el.dataset.prevTabindex;
        });
      });
    }, []);

    const settings = {
      dots: dots,
      speed: 1000,
      infinite: infinite,
      slidesToShow: slideShow || 3,
      slidesToScroll: 1,
      autoplay: autoplay,
      autoplaySpeed: autoplaySpeed,
      pauseOnHover: pauseOnHover,
      vertical: vertical,
      verticalSwiping: vertical,
      arrows: arrows,
      afterChange: (current: number) => {
        syncSlideA11y();
        if (afterChange) afterChange(current);
      },
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: slideShow || 1,
            slidesToScroll: 1
          }
        },
        {
          breakpoint: 992,
          settings: {
            slidesToShow: mobileSlideShow || slideShow || 1,
            slidesToScroll: 1
          }
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: mobileSlideShow || slideShow || 1,
            slidesToScroll: 1
          }
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: vertical ? mobileSlideShow || 3 : mobileSlideShowTwo || 1,
            slidesToScroll: 1,
            dots: true,
            className: center && 'center',
            centerMode: center && true,
            centerPadding: center && '20px'
          }
        }
      ]
    };

    useEffect(() => {
      syncSlideA11y();
      const root = wrapperRef.current;
      if (!root) return;

      const observer = new MutationObserver(() => syncSlideA11y());
      observer.observe(root, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-hidden', 'class']
      });

      return () => observer.disconnect();
    }, [syncSlideA11y]);

    return (
      <CarouselWrapper ref={wrapperRef} className='overflow-hidden carousel'>
        <Carousel
          ref={(el: any) => {
            // forward ทั้ง 2 ทาง
            if (ref) typeof ref === 'function' ? ref(el) : (ref.current = el);
            if (sliderRef) sliderRef.current = el;
          }}
          {...settings}
        >
          {children}
        </Carousel>
      </CarouselWrapper>
    );
  }
);

Carousels.displayName = 'Carousels';

const CarouselWrapper = styled.div`
  &.carousel {
    padding: 0 2px 0 2px;
  }
  .slick-slider {
    .slick-prev,
    .slick-next {
      width: 44px;
      height: 44px;
      &:before {
        color: #ed1a3b;
        font-size: 28px;
        line-height: 44px;
      }
    }
    .slick-prev {
      z-index: 1;
      left: 0;
    }
    .slick-next {
      z-index: 1;
      right: 7px;
    }
  }
  .slick-dots {
    .slick-active {
      button:before {
        color: rgba(0, 0, 0, 1);
      }
    }
    li.slick-active {
      button:before {
        background-color: rgb(220 21 53);
        opacity: 1;
      }
    }
    button:before {
      content: '';
      color: rgba(0, 0, 0, 0.8);
      background-color: rgb(191 191 191);
      border-radius: 100%;
      opacity: 1;
      width: 15px;
      height: 15px;
    }
  }
  .slick-track {
    overflow: hidden;
  }
`;

export default Carousels;
